import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdCheckCircle, MdDeleteForever, MdEdit, MdEmail, MdLocationOn, MdPhone, MdVerified } from 'react-icons/md';
import { getData, putData, deleteData } from '../../Webservice/Webservice';
import { CustomerAvatar, FONT, StatusChip, TypeChip, authToken, displayId, fmtDate, fmtDateTime, inr } from './customerShared';

const Row = ({ k, v, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
    <Typography sx={{ fontFamily: mono ? 'monospace' : FONT, fontSize: 13, fontWeight: 700, color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>{v === undefined || v === null || v === '' ? '—' : v}</Typography>
  </Box>
);

const Card = ({ title, children, sx }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', ...sx }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, mb: 1 }}>{title}</Typography>
    {children}
  </Paper>
);

const ORDER_STATUS = { delivered: ['#dcfce7', '#166534'], cancelled: ['#fee2e2', '#991b1b'], returned: ['#ffedd5', '#9a3412'], pending: ['#fef3c7', '#92400e'] };

function CustomerDetails() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getData(`customer/${customerId}`, null, authToken(), false);
      if (!res?._id) throw new Error('missing');
      setC(res);
      setError('');
    } catch (e) {
      setError('Could not load this customer.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (changes) => {
    setBusy(true);
    try {
      await putData(`customer/statusUpdate/${customerId}`, changes, authToken());
      setC((x) => ({ ...x, ...changes }));
    } catch (e) {
      setError('Could not update the customer.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteData(`customer/delete/${customerId}`, authToken());
      navigate('/customermanagement');
    } catch (e) {
      setError('Could not delete the customer.');
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error && !c) return <Box sx={{ p: 3 }}><Button startIcon={<MdArrowBack />} onClick={() => navigate('/customermanagement')} sx={{ textTransform: 'none', fontWeight: 700 }}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></Box>;

  const business = c.customerType === 'business';
  const addresses = c.addresses || [];
  const tabs = ['Overview', `Addresses (${addresses.length})`, `Orders (${c.totalOrders || 0})`, ...(business ? ['Business'] : [])];

  const kpis = [
    { label: 'Orders', value: c.totalOrders || 0, bg: '#eef2ff', fg: '#4338ca' },
    { label: 'Total spent', value: inr(c.totalSpent), bg: '#d1fae5', fg: '#047857' },
    { label: 'Average order', value: inr(c.averageOrderValue), bg: '#e0f2fe', fg: '#0369a1' },
    { label: 'Last order', value: c.lastOrderAt ? fmtDate(c.lastOrderAt) : 'Never', bg: '#fef3c7', fg: '#b45309' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <IconButton onClick={() => navigate('/customermanagement')} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Customers / <b style={{ color: '#0f172a' }}>{c.name || 'Unnamed customer'}</b></Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '16px', color: '#fff', background: 'linear-gradient(135deg,#0f172a,#334155)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.5 }}>
          <CustomerAvatar customer={c} size={72} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, lineHeight: 1.2 }}>{c.name || 'Unnamed customer'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.8 }}>{displayId(c)} · joined {fmtDate(c.createdAt)}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TypeChip type={c.customerType} />
              <StatusChip status={c.status} />
              {c.isVerified && <Chip size="small" icon={<MdVerified />} label="Verified" sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: '#dbeafe', color: '#1d4ed8', '& .MuiChip-icon': { color: 'inherit' } }} />}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/customermanagement', { state: { editId: customerId } })} startIcon={<MdEdit />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: 'rgb(255 209 2)', color: '#000', '&:hover': { background: 'rgb(255 190 0)' } }}>Edit</Button>
            <Button onClick={() => setConfirmDelete(true)} startIcon={<MdDeleteForever />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: 'rgba(248,113,113,.2)', color: '#fecaca', '&:hover': { background: 'rgba(248,113,113,.35)' } }}>Delete</Button>
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} sx={{ p: 2, borderRadius: '14px', background: k.bg }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: k.fg, opacity: 0.85 }}>{k.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 21, fontWeight: 800, color: k.fg }}>{k.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, '& .MuiTab-root': { fontFamily: FONT, fontWeight: 800, textTransform: 'none', fontSize: 14 }, '& .MuiTabs-indicator': { background: 'rgb(255 190 0)', height: 3 } }}>
          {tabs.map((t) => <Tab key={t} label={t} />)}
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Contact & personal">
              <Row k="Email" v={<><MdEmail style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.email}</>} />
              <Row k="Mobile" v={<><MdPhone style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.mobile}</>} />
              <Row k="Gender" v={c.gender ? c.gender[0].toUpperCase() + c.gender.slice(1) : ''} />
              <Row k="Date of birth" v={c.dob ? fmtDate(c.dob) : ''} />
            </Card>
            <Card title="Account">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Account active</Typography>
                <Switch size="small" disabled={busy} checked={c.status === 'active'} onChange={() => patch({ status: c.status === 'active' ? 'inactive' : 'active' })} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Verified</Typography>
                <Switch size="small" disabled={busy} checked={!!c.isVerified} onChange={() => patch({ isVerified: !c.isVerified })} />
              </Box>
              <Row k="Signed up with" v={c.provider} />
              <Row k="Source" v={c.customerSource === 'warehouse' ? 'Warehouse' : 'Mobile app'} />
              <Row k="Created by" v={c.createdBy ? `${c.createdBy.firstName || ''} ${c.createdBy.lastName || ''}`.trim() : 'Self sign-up'} />
              <Row k="Last updated" v={fmtDateTime(c.updatedAt)} />
            </Card>
            <Card title="Communication preferences" sx={{ gridColumn: { md: '1 / -1' } }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[['Promotional emails', c.emailPromotion], ['WhatsApp promotions', c.whatsappPromotion], ['App notifications', c.appNotification]].map(([label, on]) => (
                  <Chip key={label} icon={on !== false ? <MdCheckCircle /> : undefined} label={`${label}: ${on !== false ? 'on' : 'off'}`} sx={{ fontFamily: FONT, fontWeight: 700, background: on !== false ? '#dcfce7' : '#f1f5f9', color: on !== false ? '#166534' : '#64748b', '& .MuiChip-icon': { color: 'inherit' } }} />
                ))}
              </Box>
            </Card>
          </Box>
        )}

        {tab === 1 && (
          addresses.length === 0 ? (
            <Paper elevation={0} sx={{ py: 6, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>No saved addresses.</Typography></Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {addresses.map((a) => (
                <Paper key={a._id} elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: `1.5px solid ${a.isDefault ? 'rgb(255 190 0)' : '#e2e8f0'}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <MdLocationOn color="#64748b" />
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, textTransform: 'capitalize' }}>{a.type || 'home'}</Typography>
                    {a.isDefault && <Chip size="small" label="Default" sx={{ fontFamily: FONT, fontWeight: 700, height: 20, background: 'rgb(255 209 2 / 25%)' }} />}
                  </Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700 }}>{a.name}{a.mobile ? ` · ${a.mobile}` : ''}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                    {[a.addressLine1, a.addressLine2, a.landmark].filter(Boolean).join(', ')}<br />
                    {[a.city, a.state].filter(Boolean).join(', ')} {a.pincode ? `– ${a.pincode}` : ''}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )
        )}

        {tab === 2 && (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT } }}>
                  <TableCell>Order</TableCell><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(c.recentOrders || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: '#64748b', fontFamily: FONT }}>No orders yet.</TableCell></TableRow>
                ) : c.recentOrders.map((o) => {
                  const [bg, fg] = ORDER_STATUS[o.orderStatus] || ['#e0e7ff', '#3730a3'];
                  return (
                    <TableRow key={o._id} hover onClick={() => navigate(`/ordermanagement/${o._id}`)} sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: 13 } }}>
                      <TableCell sx={{ fontWeight: 800 }}>{o.orderNumber}</TableCell>
                      <TableCell>{fmtDateTime(o.createdAt)}</TableCell>
                      <TableCell><Chip size="small" label={o.orderStatus} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, textTransform: 'capitalize', background: bg, color: fg }} /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{inr(o.totalAmount)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {(c.totalOrders || 0) > (c.recentOrders || []).length && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', p: 1.5, textAlign: 'center' }}>Showing the latest {c.recentOrders.length} of {c.totalOrders} orders.</Typography>}
          </TableContainer>
        )}

        {business && tab === 3 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Business">
              <Row k="Business name" v={c.businessName} />
              <Row k="Type" v={(c.businessType || '').replace(/_/g, ' ')} />
              <Row k="Registration number" v={c.businessRegistrationNumber} />
            </Card>
            <Card title="Tax details">
              <Row k="GST number" v={c.gstNumber} mono />
              <Row k="GST verified" v={c.gstNumber ? (c.gstVerified ? 'Yes' : 'No') : ''} />
              <Row k="PAN" v={c.panNumber} mono />
              <Row k="PAN verified" v={c.panNumber ? (c.panVerified ? 'Yes' : 'No') : ''} />
            </Card>
          </Box>
        )}
      </Box>

      <Dialog open={confirmDelete} onClose={() => !busy && setConfirmDelete(false)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete customer?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT }}><b>{c.name || 'This customer'}</b> will be removed and archived. They will no longer be able to sign in.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={remove} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Deleting…' : 'Yes, delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerDetails;
